import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  Grid,
  Paper,
  Avatar,
  IconButton,
  Button,
  Autocomplete,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  ListItemButton,
  ListItemText
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { createFarmerAction, getAllFarmersAction, getAllDistrictsAction, getAllVillagesAction, getAllMandalsAction, clearCreateFarmerAction, updateFarmerAction, clearUpdateFarmerAction, getAllCropsAction } from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import * as Yup from "yup";

// Inline Yup validation schema
const farmerValidationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(3, "Minimum 3 characters"),
  mobile: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit mobile number")
    .required("Mobile number is required"),
  whatsappNo: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter valid WhatsApp number")
    .optional(),
  districtName: Yup.string().required("District is required"),
  districtId: Yup.string().optional(),
  mandalName: Yup.string().required("Mandal is required"),
  mandalId: Yup.string().optional(),
  villageName: Yup.string().required("Village is required"),
  villageId: Yup.string().optional(),
  geoCode: Yup.string().optional(),
  totalAcres: Yup.number()
    .typeError("Enter valid acres")
    .positive("Must be positive")
    .optional(),
  acresUnderIrrigation: Yup.string().optional(),
  irrigationType: Yup.string()
    .oneOf(["rainfed", "irrigation"], "Select rainfed or irrigation")
    .required("Irrigation type is required"),
  existingUser: Yup.boolean()
    .required("Existing user is required"),
  majorCrops: Yup.array()
    .of(Yup.string().min(2, "Crop name too short"))
    .min(1, "Select at least one crop")
    .required("Major crops are required"),
  focusCrop: Yup.string().required("Focus crop is required"),
  avatar: Yup.string().optional()
});

/* ── Search-or-Create Location Input ── */
const LocationSearchInput = ({
  label, value, selectedId, suggestions,
  onTextChange, onSelect, disabled, error, helperText,
}) => {
  const [focused, setFocused] = useState(false);

  const filtered = useMemo(() => {
    if (!value?.trim()) return [];
    return suggestions.filter((s) =>
      s.label.toLowerCase().includes(value.toLowerCase().trim())
    );
  }, [value, suggestions]);

  // Auto-select if typed text is an exact case-insensitive match
  const handleTextChange = (text) => {
    onTextChange(text);
    if (text.trim()) {
      const match = suggestions.find(
        (s) => s.label.toLowerCase() === text.trim().toLowerCase()
      );
      if (match) {
        onSelect(match);
      }
    }
  };

  const showSuggestions = focused && value?.trim().length > 0 && filtered.length > 0;
  const isNew = value?.trim().length > 0 && !selectedId;

  let displayHelper = "";
  let helperColor;
  if (error && helperText) {
    displayHelper = helperText;
    helperColor = "error.main";
  } else if (selectedId) {
    displayHelper = `✓ Existing ${label.toLowerCase()} selected`;
    helperColor = "success.main";
  } else if (isNew) {
    displayHelper = `New ${label.toLowerCase()} will be created`;
    helperColor = "info.main";
  }

  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value}
        onChange={(e) => handleTextChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        disabled={disabled}
        error={error}
        helperText={displayHelper}
        placeholder={`Search or create ${label.toLowerCase()}`}
        FormHelperTextProps={{ sx: { color: helperColor } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            backgroundColor: "#FAFAFA",
          },
        }}
      />
      {showSuggestions && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            zIndex: 1300,
            width: "100%",
            maxHeight: 200,
            overflow: "auto",
            mt: 0.5,
            borderRadius: 1,
          }}
        >
          {filtered.map((item) => (
            <ListItemButton
              key={item.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(item);
                setFocused(false);
              }}
              sx={{ py: 0.5, px: 1.5 }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItemButton>
          ))}
        </Paper>
      )}
    </Box>
  );
};

const AMRMFarmerCreation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const farmerId = location.state?.farmerId || null;


  const [majorCropInput, setMajorCropInput] = useState("");



  // --- Geo location ---
  const [geoLocating, setGeoLocating] = useState(false);

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setGeoLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        formik.setFieldValue("geoCode", `${latitude},${longitude}`);
        setGeoLocating(false);
      },
      (error) => {
        toast.error("Unable to retrieve location: " + error.message);
        setGeoLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const { successData: districts } = useSelector(state => state.getAllDistrictsReducer);
  const districtList = useMemo(() => districts?.data?.districts || [], [districts]);

  const { successData: mandals } = useSelector(state => state.getAllMandalsReducer);
  const mandalList = useMemo(() => mandals?.data?.mandals || [], [mandals]);

  const { successData: villages } = useSelector(state => state.getAllVillagesReducer);
  const villageList = useMemo(() => villages?.data?.villages || [], [villages]);

  const { successData: cropsData } = useSelector(state => state.getAllCropsReducer);
  const cropsList = useMemo(() => cropsData?.data?.crops?.map(c => c.name) || [], [cropsData]);

  const { successData: createdFarmerData, error: createdFarmerError, errorInfo: createdFarmerErrorInfo } = useSelector(state => state.createFarmerReducer);
  const { successData: updatedFarmerData, error: updatedFarmerError, errorInfo: updatedFarmerErrorInfo } = useSelector(state => state.updateFarmerReducer);
  const { successData: getFarmerData } = useSelector((state) => state.getAllFarmersReducer);

  

  const formik = useFormik({
    initialValues: {
      villageId: getFarmerData?.data?.villageId || "",
      villageName: "",
      mandalId: getFarmerData?.data?.mandalId || "",
      mandalName: "",
      districtId: getFarmerData?.data?.districtId || "",
      districtName: "",
      name: getFarmerData?.data?.name || "",
      mobile: getFarmerData?.data?.mobile || "",
      whatsappNo: getFarmerData?.data?.whatsappNo || "",
      existingUser: Boolean(getFarmerData?.data?.existingUser),
      majorCrops: Array.isArray(getFarmerData?.data?.majorCrops) ? getFarmerData.data.majorCrops : [],
      focusCrop: getFarmerData?.data?.focusCrop || "",
      irrigationType: getFarmerData?.data?.irrigationType || "",
      acresUnderIrrigation: getFarmerData?.data?.acresUnderIrrigation ?? "",
      geoCode: getFarmerData?.data?.geoCode || "",
      avatar: getFarmerData?.data?.avatar || "",
      totalAcres: getFarmerData?.data?.totalAcres ?? ""
    },
    enableReinitialize: true,
    validationSchema: farmerValidationSchema,
    onSubmit: (values) => {
      // existing selection → send ID; new text → send name string
      const districtValue = values.districtId ? values.districtId : values.districtName.trim();
      const mandalValue   = values.mandalId   ? values.mandalId   : values.mandalName.trim();
      const villageValue  = values.villageId  ? values.villageId  : values.villageName.trim();

      const payload = {
        name: values.name,
        mobile: values.mobile,
        whatsappNo: values.whatsappNo,
        existingUser: values.existingUser,
        majorCrops: values.majorCrops,
        focusCrop: values.focusCrop,
        irrigationType: values.irrigationType,
        acresUnderIrrigation: values.acresUnderIrrigation,
        geoCode: values.geoCode,
        avatar: values.avatar,
        districtId: districtValue,
        mandalId: mandalValue,
        villageId: villageValue,
        totalAcres:
          values.totalAcres === "" || values.totalAcres === null
            ? null
            : Number.parseInt(values.totalAcres, 10)
      };

      if (farmerId) {
        dispatch(updateFarmerAction({ endPoint: `farmer/${farmerId}`, ...payload }));
      } else {
        dispatch(createFarmerAction(payload));
      }
    }
  });

  // Autofill when farmer data is fetched (guarded to avoid render loops)
  const prefilledRef = useRef(null);
  useEffect(() => {
    const d = getFarmerData?.data;
    if (!d) return;

    // Only prefill once per farmerId
    if (prefilledRef.current === farmerId) return;
    if (!districtList?.length) return;

    formik.setValues((prev) => ({
      ...prev,
      districtId: d.districtId || "",
      districtName: districtList.find(x => x.districtId === d.districtId)?.name || "",
      mandalId: d.mandalId || "",
      mandalName: mandalList.find(x => x.mandalId === d.mandalId)?.name || "",
      villageId: d.villageId || "",
      villageName: villageList.find(x => x.villageId === d.villageId)?.name || "",
      // Personal info
      name: d.name || "",
      mobile: d.mobile || "",
      whatsappNo: d.whatsappNo || "",
      existingUser: Boolean(d.existingUser),
      // Agriculture profile
      majorCrops: Array.isArray(d.majorCrops) ? d.majorCrops : [],
      focusCrop: d.focusCrop || "",
      irrigationType: d.irrigationType || "",
      acresUnderIrrigation: d.acresUnderIrrigation ?? "",
      totalAcres: d.totalAcres ?? "",
      // Misc
      geoCode: d.geoCode || "",
      avatar: d.avatar || ""
    }));

    prefilledRef.current = farmerId;
  }, [getFarmerData, districtList, farmerId, formik]);

  /* Normalised suggestion lists for LocationSearchInput */
  const districtSuggestions = useMemo(() =>
    districtList.map(d => ({ id: d.districtId, label: d.name || "" })).filter(d => d.id && d.label),
    [districtList]
  );

  const mandalSuggestions = useMemo(() => {
    if (!formik.values.districtId) return [];
    return mandalList
      .filter(m => String(m.districtId) === String(formik.values.districtId))
      .map(m => ({ id: m.mandalId, label: m.name || "" }))
      .filter(m => m.id && m.label);
  }, [mandalList, formik.values.districtId]);

  const villageSuggestions = useMemo(() => {
    if (!formik.values.mandalId) return [];
    return villageList
      .filter(v => String(v.mandalId) === String(formik.values.mandalId))
      .map(v => ({ id: v.villageId, label: v.name || "" }))
      .filter(v => v.id && v.label);
  }, [villageList, formik.values.mandalId]);


  useEffect(() => {
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
    dispatch(getAllVillagesAction());
    dispatch(getAllCropsAction());
  }, [dispatch]);

  useEffect(() => {
    if (farmerId) {
      dispatch(getAllFarmersAction(
        { endPoint: `farmer/${farmerId}` },
      ))
    }
  }, [farmerId, dispatch]);


  useEffect(() => {
    if (createdFarmerData) {
      toast.success(createdFarmerData.message || "Farmer created successfully");
      dispatch(clearCreateFarmerAction());
      navigate('/amrm/farmerManagement');
    }
  }, [createdFarmerData, dispatch, navigate]);

  useEffect(() => {
    if (createdFarmerError) {
      toast.error(createdFarmerErrorInfo || "Failed to create farmer");
      dispatch(clearCreateFarmerAction());
    }
  }, [createdFarmerError, dispatch, createdFarmerErrorInfo]);

  useEffect(() => {
    if (updatedFarmerData) {
      toast.success(updatedFarmerData.message || "Farmer updated successfully");
      dispatch(clearUpdateFarmerAction());
      navigate('/amrm/farmerManagement');
    }
  }, [updatedFarmerData, dispatch, navigate]);

  useEffect(() => {
    if (updatedFarmerError) {
      toast.error(updatedFarmerErrorInfo || "Failed to update farmer");
      dispatch(clearUpdateFarmerAction());
    }
  }, [updatedFarmerError, dispatch, updatedFarmerErrorInfo]);



  return (
    <Paper
      elevation={0}
      sx={{
        mx: "auto",
        p: { xs: 2, sm: 3, md: 4 }, // 🔹 mobile padding
        mt: { xs: 8, sm: 6, md: 0 },
        borderRadius: 3,
        position: "relative"
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={() => navigate(-1)}
        sx={{
          position: "absolute",
          top: { xs: 2, sm: 10, md: 4 },
          right: { xs: 2, sm: 10, md: 4 },
          bgcolor: "#F3F4F6",
          "&:hover": { bgcolor: "#E5E7EB" }
        }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      {/* Header */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }} // 🔹 stack on mobile
        gap={2}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        mt={{ xs: 0, sm: 4, md: 0 }}
      >
        <Box display="flex" gap={1.5}>
          <Avatar sx={{ bgcolor: "black", width: 32, height: 32 }}>
            <CheckCircleIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography fontWeight={600}>
              Farmer Information Management
            </Typography>
            <Typography fontSize={14} color="text.secondary">
              Register and manage detailed agricultural profiles for farmers.
            </Typography>
          </Box>
        </Box>

        <Chip
          label="System Active"
          sx={{
            bgcolor: "#D1FAE5",
            color: "#065F46",
            width: "fit-content"
          }}
        />
      </Box>

      <form onSubmit={formik.handleSubmit}>
        {/* Personal Information */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2} mt={4}>
          <PersonIcon fontSize="small" />
          <Typography fontWeight={600}>Personal Information</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Name
            </Typography>
            <TextField
              size="small"
              fullWidth
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter name"
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#FAFAFA"
                }
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Mobile
            </Typography>
            <TextField
              size="small"
              fullWidth
              name="mobile"
              value={formik.values.mobile}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter mobile number"
              error={formik.touched.mobile && Boolean(formik.errors.mobile)}
              helperText={formik.touched.mobile && formik.errors.mobile}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#FAFAFA"
                }
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              WhatsApp Number
            </Typography>
            <TextField
              size="small"
              fullWidth
              name="whatsappNo"
              value={formik.values.whatsappNo}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter WhatsApp number"
              error={formik.touched.whatsappNo && Boolean(formik.errors.whatsappNo)}
              helperText={formik.touched.whatsappNo && formik.errors.whatsappNo}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#FAFAFA"
                }
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Existing User
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formik.values.existingUser)}
                  onChange={(e) => formik.setFieldValue("existingUser", e.target.checked)}
                  name="existingUser"
                  color="primary"
                />
              }
              label={formik.values.existingUser ? "Yes" : "No"}
            />
          </Grid>
        </Grid>

        {/* Location Details */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2} mt={4}>
          <LocationOnIcon fontSize="small" />
          <Typography fontWeight={600}>Location Details</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <LocationSearchInput
              label="District"
              value={formik.values.districtName}
              selectedId={formik.values.districtId}
              suggestions={districtSuggestions}
              disabled={false}
              error={formik.touched.districtName && Boolean(formik.errors.districtName)}
              helperText={formik.touched.districtName && formik.errors.districtName}
              onTextChange={(text) => {
                formik.setFieldValue("districtName", text);
                formik.setFieldValue("districtId", "");
                formik.setFieldTouched("districtName", true, false);
                formik.setFieldValue("mandalName", "");
                formik.setFieldValue("mandalId", "");
                formik.setFieldValue("villageName", "");
                formik.setFieldValue("villageId", "");
              }}
              onSelect={(item) => {
                formik.setFieldValue("districtName", item.label);
                formik.setFieldValue("districtId", item.id);
                formik.setFieldTouched("districtName", true, false);
                formik.setFieldValue("mandalName", "");
                formik.setFieldValue("mandalId", "");
                formik.setFieldValue("villageName", "");
                formik.setFieldValue("villageId", "");
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <LocationSearchInput
              label="Mandal"
              value={formik.values.mandalName}
              selectedId={formik.values.mandalId}
              suggestions={mandalSuggestions}
              disabled={!formik.values.districtName?.trim()}
              error={formik.touched.mandalName && Boolean(formik.errors.mandalName)}
              helperText={formik.touched.mandalName && formik.errors.mandalName}
              onTextChange={(text) => {
                formik.setFieldValue("mandalName", text);
                formik.setFieldValue("mandalId", "");
                formik.setFieldTouched("mandalName", true, false);
                formik.setFieldValue("villageName", "");
                formik.setFieldValue("villageId", "");
              }}
              onSelect={(item) => {
                formik.setFieldValue("mandalName", item.label);
                formik.setFieldValue("mandalId", item.id);
                formik.setFieldTouched("mandalName", true, false);
                formik.setFieldValue("villageName", "");
                formik.setFieldValue("villageId", "");
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <LocationSearchInput
              label="Village"
              value={formik.values.villageName}
              selectedId={formik.values.villageId}
              suggestions={villageSuggestions}
              disabled={!formik.values.mandalName?.trim()}
              error={formik.touched.villageName && Boolean(formik.errors.villageName)}
              helperText={formik.touched.villageName && formik.errors.villageName}
              onTextChange={(text) => {
                formik.setFieldValue("villageName", text);
                formik.setFieldValue("villageId", "");
                formik.setFieldTouched("villageName", true, false);
              }}
              onSelect={(item) => {
                formik.setFieldValue("villageName", item.label);
                formik.setFieldValue("villageId", item.id);
                formik.setFieldTouched("villageName", true, false);
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>

            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  flex: 1,
                  px: 1.5,
                  py: 0.9,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: formik.touched.geoCode && formik.errors.geoCode ? "error.main" : "#E0E0E0",
                  minHeight: 25,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <Typography
                  fontSize={13}
                  color={formik.values.geoCode ? "text.primary" : "text.disabled"}
                  noWrap
                >
                  {formik.values.geoCode || "Auto-filled on get location"}
                </Typography>
              </Box>
              <Tooltip title="Get current location">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={getLocation}
                    disabled={geoLocating}
                  >
                    {geoLocating
                      ? <CircularProgress size={18} />
                      : <MyLocationIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {formik.touched.geoCode && formik.errors.geoCode && (
              <Typography fontSize={11} color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {formik.errors.geoCode}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Agriculture Profile */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2} mt={4}>
          <AgricultureIcon fontSize="small" />
          <Typography fontWeight={600}>Agriculture Profile</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Major Crops
            </Typography>
            <Autocomplete
              multiple
              options={cropsList}
              value={formik.values.majorCrops || []}
              onChange={(_, newValue) => {
                formik.setFieldValue("majorCrops", newValue || []);
                formik.setFieldTouched("majorCrops", true, false);
              }}
              onBlur={() => formik.setFieldTouched("majorCrops", true)}
              filterSelectedOptions
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search and select crops"
                  error={formik.touched.majorCrops && Boolean(formik.errors.majorCrops)}
                  helperText={formik.touched.majorCrops && formik.errors.majorCrops}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FAFAFA"
                    }
                  }}
                />
              )}
            />
            <Box mt={1} display="flex" gap={1} flexWrap="wrap">
              {(formik.values.majorCrops || []).map((option, idx) => (
                <Chip
                  key={option}
                  label={option}
                  onDelete={() => {
                    const next = (formik.values.majorCrops || []).filter((c, i) => i !== idx);
                    formik.setFieldValue("majorCrops", next);
                  }}
                  size="small"
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Box>
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Irrigation Type
            </Typography>
            <Autocomplete
              options={["rainfed", "irrigation"]}
              value={formik.values.irrigationType || null}
              onChange={(_, newValue) => formik.setFieldValue("irrigationType", newValue || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Select irrigation type"
                  error={formik.touched.irrigationType && Boolean(formik.errors.irrigationType)}
                  helperText={formik.touched.irrigationType && formik.errors.irrigationType}
                />
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Total Acres
            </Typography>
            <TextField
              size="small"
              fullWidth
              name="totalAcres"
              value={formik.values.totalAcres}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Total acres"
              type="text"
              inputMode="decimal"
              error={formik.touched.totalAcres && Boolean(formik.errors.totalAcres)}
              helperText={formik.touched.totalAcres && formik.errors.totalAcres}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#FAFAFA"
                }
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 6, }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Acres Under Irrigation
            </Typography>
            <TextField
              size="small"
              fullWidth
              name="acresUnderIrrigation"
              value={formik.values.acresUnderIrrigation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Acres under irrigation"
              error={formik.touched.acresUnderIrrigation && Boolean(formik.errors.acresUnderIrrigation)}
              helperText={formik.touched.acresUnderIrrigation && formik.errors.acresUnderIrrigation}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#FAFAFA"
                }
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 0.5 }}>
              Focus Crop
            </Typography>
            <Autocomplete
              options={cropsList}
              value={formik.values.focusCrop || null}
              onChange={(_, newValue) => {
                formik.setFieldValue("focusCrop", newValue || "");
                formik.setFieldTouched("focusCrop", true, false);
              }}
              onBlur={() => formik.setFieldTouched("focusCrop", true)}
              getOptionLabel={(option) => option || ""}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search and select focus crop"
                  error={formik.touched.focusCrop && Boolean(formik.errors.focusCrop)}
                  helperText={formik.touched.focusCrop && formik.errors.focusCrop}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FAFAFA"
                    }
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button variant="outlined" sx={{ mr: 2 }}
            onClick={() => {
              navigate('/amrm/farmerManagement');
            }}>
            cancel
          </Button>
          <Button type="submit" variant="contained">
            {farmerId ? "Update" : "Submit"}
          </Button>
        </Box>
      </form>


    </Paper>
  );
};

export default AMRMFarmerCreation;
